/**
 * TODO: Replace this local IntegrationBanner with @fluentsms/agentnet-web-components once
 * the MUI and React version upgrade is complete. AgentNetPortal currently uses Material-UI v4
 * and React 17, while lib-agentnet targets MUI v7 and React 19, so the shared component
 * cannot be used directly today. Vahe is working on the upgrade, but it will take time;
 * once that work lands, switch back to the lib-agentnet IntegrationBanner.
 */
import { Box, Button, Card, CardContent, Chip, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { FC, ReactNode } from 'react';
import type { StatusChipConfig } from './IntegrationCard';

interface IntegrationBannerProps {
  description: string;
  category: string;
  enabled: boolean;
  fullWidth?: boolean;
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title: string;
  dataTestId?: string;
  actionButtonDataTestId?: string;
  enableLabel?: string;
  disableLabel?: string;
  statusChips?: StatusChipConfig[];
}

const useStyles = makeStyles((theme: Theme) => ({
  rootCard: {
    borderRadius: '6px',
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  iconCard: {
    alignItems: 'center',
    backgroundColor: theme.palette.brand.feather,
    border: 'none',
    borderRadius: '6px',
    display: 'flex',
    height: 150,
    justifyContent: 'center',
    width: 150,
    '& svg': {
      height: 75,
      width: 'auto',
    },
  },
  textGrid: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  description: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actionsRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  disableButton: {
    borderColor: `${theme.palette.error.main} !important`,
    color: `${theme.palette.error.main} !important`,
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: `${theme.palette.error.light} !important`,
    },
    '&.Mui-disabled': {
      borderColor: `${theme.palette.error.main} !important`,
      color: `${theme.palette.error.main} !important`,
      opacity: 0.5,
    },
  },
  successChip: {
    backgroundColor: '#E8F5E9',
    color: theme.palette.success.dark,
    fontSize: theme.typography.pxToRem(12),
    '& .MuiChip-icon': {
      color: theme.palette.success.dark,
    },
  },
}));

const IntegrationBanner: FC<IntegrationBannerProps> = ({
  description,
  enabled,
  icon,
  onClick,
  title,
  disabled = false,
  dataTestId,
  actionButtonDataTestId,
  enableLabel = 'Enable',
  disableLabel = 'Disable',
  statusChips,
}) => {
  const classes = useStyles();
  const actionButtonLabel = enabled ? disableLabel : enableLabel;

  return (
    <Card className={classes.rootCard} variant="outlined" data-testid={dataTestId}>
      <CardContent className={classes.cardContent}>
        <Grid container spacing={3}>
          <Grid item>
            <Card className={classes.iconCard} variant="outlined">
              {icon && icon}
            </Card>
          </Grid>
          <Grid item className={classes.textGrid}>
            <Box>
              <Typography variant="h2">{title}</Typography>
              <Typography variant="body1" className={classes.description}>
                {description}
              </Typography>
              <Box className={classes.actionsRow}>
                <Button
                  className={enabled ? classes.disableButton : undefined}
                  color="primary"
                  variant={enabled ? 'outlined' : 'contained'}
                  size="small"
                  onClick={onClick}
                  disabled={disabled}
                  data-testid={actionButtonDataTestId ?? (dataTestId ? `${dataTestId}-button` : undefined)}
                >
                  {actionButtonLabel}
                </Button>
                {statusChips?.map((chip, index) => (
                  <Chip
                    key={chip.testId ?? `${chip.label}-${index}`}
                    className={classes.successChip}
                    label={chip.label}
                    {...(chip.icon ? { icon: chip.icon } : {})}
                    size="small"
                    data-testid={chip.testId}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default IntegrationBanner;
